import { BASIS } from "./basis";
import {
  adresseMitHerkunft,
  herkunftWaehlen,
  traegtZuordnung,
} from "./herkunft";
/**
 * Was im Funnel passiert, an den Server melden.
 *
 * Drei Dinge machen den Unterschied zwischen brauchbaren und unbrauchbaren
 * Zahlen:
 *
 *   1. **Die Kennungen kommen von hier.** Sitzung und Ereignisse bekommen im
 *      Browser eine UUID. Dadurch kann gesammelt werden, bevor der Server
 *      geantwortet hat, und doppelt Gesendetes verwirft sich dort am
 *      Primärschlüssel statt doppelt gezählt zu werden.
 *   2. **Gebündelt statt einzeln.** Jedes Ereignis sofort zu senden wäre am
 *      Handy im Mobilfunknetz spürbar. Es wird gesammelt und alle paar
 *      Sekunden abgeschickt.
 *   3. **Beim Verlassen der Seite über sendBeacon.** Ein normales fetch wird
 *      beim Schließen des Tabs abgebrochen, und ausgerechnet dieser Moment
 *      ist der interessanteste: Wer wegklickt, ist ein Abbruch, und ohne
 *      diese Meldung weiß man nicht, wo.
 *
 * Nichts hiervon darf den Funnel aufhalten. Jeder Fehler wird verschluckt: Ein
 * verlorenes Ereignis kostet eine Zeile in der Statistik, ein blockierter
 * Funnel kostet eine Anfrage.
 */

/** Schlüssel für die Sitzung, die dieser Browser gerade führt. */
const SITZUNG_SCHLUESSEL = "fbs.sitzung";

export type Ereignisart =
  | "funnel_view"
  | "funnel_started"
  | "step_viewed"
  | "answer_selected"
  | "step_completed"
  | "step_forward"
  | "step_back"
  | "validation_error"
  | "contact_started"
  | "lead_submitted"
  | "funnel_completed"
  | "page_exit"
  | "funnel_resumed"
  | "step_hesitation"
  | "option_changed";

interface Ereignis {
  id: string;
  type: Ereignisart;
  step_id?: string;
  previous_step_id?: string;
  next_step_id?: string;
  step_index?: number;
  time_on_step_ms?: number;
  occurred_at: string;
  payload?: Record<string, unknown>;
}

export interface EreignisAngabe {
  stepId?: string | undefined;
  previousStepId?: string | undefined;
  nextStepId?: string | undefined;
  stepIndex?: number | undefined;
  timeOnStepMs?: number | undefined;
  payload?: Record<string, unknown> | undefined;
}

function kennung(): string {
  // crypto.randomUUID gibt es in jedem Browser, der diese Anwendung überhaupt
  // ausführt. Der Rückfall ist für sehr alte Umgebungen und für Tests.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const zufall = () =>
    Math.floor(Math.random() * 0x10000)
      .toString(16)
      .padStart(4, "0");
  return `${zufall()}${zufall()}-${zufall()}-4${zufall().slice(1)}-a${zufall().slice(1)}-${zufall()}${zufall()}${zufall()}`;
}

/**
 * Die Sitzung, die dieser Browser führt.
 *
 * Sie überlebt ein Neuladen, damit ein Besucher, der die Seite aktualisiert,
 * nicht als zweiter Besucher gezählt wird. Nach einer Stunde ohne Aktivität
 * gilt der nächste Aufruf als neuer Besuch: Das ist die Grenze, ab der jemand
 * nicht mehr dieselbe Aufgabe fortsetzt, sondern neu anfängt.
 */
const SITZUNG_GUELTIG_MS = 60 * 60 * 1000;

interface Sitzungsmerkung {
  id: string;
  zuletzt: number;
  /** Die Zuordnung der ersten Sitzung, für den Fall einer Rückkehr. */
  landing?: string;
  referrer?: string;
}

function merkungLesen(): Sitzungsmerkung | null {
  try {
    const roh = window.localStorage.getItem(SITZUNG_SCHLUESSEL);
    if (!roh) return null;
    const wert = JSON.parse(roh) as Partial<Sitzungsmerkung>;
    if (typeof wert.id !== "string" || typeof wert.zuletzt !== "number") return null;
    return {
      id: wert.id,
      zuletzt: wert.zuletzt,
      ...(typeof wert.landing === "string" ? { landing: wert.landing } : {}),
      ...(typeof wert.referrer === "string" ? { referrer: wert.referrer } : {}),
    };
  } catch {
    return null;
  }
}

function merkungSchreiben(merkung: Sitzungsmerkung): void {
  try {
    window.localStorage.setItem(SITZUNG_SCHLUESSEL, JSON.stringify(merkung));
  } catch {
    // Privater Modus oder voller Speicher. Dann eben eine Sitzung je Aufruf.
  }
}

/**
 * Die Herkunft, die der Behälter auf der Seite angibt.
 *
 * Gelesen wird sie aus dem Markup und nicht aus der Adresse: Ein Ratgeber
 * soll unter seiner sauberen Adresse im Index stehen, das Quiz darin aber
 * trotzdem erkennbar vom Beitrag kommen. Der Zusammenbau der WordPress-Seite
 * schreibt das Attribut aus der Marke {{FUNNEL:<quiz>;utm_source=...}}.
 */
function herkunftDesBehaelters(): string {
  const behaelter = document.querySelector<HTMLElement>("[data-herkunft]");
  return behaelter?.dataset["herkunft"] ?? "";
}

export class Tracker {
  private sitzungId = kennung();
  private warteschlange: Ereignis[] = [];
  private bereit = false;
  private uhr: number | null = null;
  private schrittSeit = Date.now();
  private aktuellerSchritt: string | null = null;
  private beendet = false;
  private vorherigeSitzung: string | null = null;
  private zuordnungWiederhergestellt = false;
  private landing: string;
  private referrer: string;
  /**
   * Die Seite, auf der dieser Funnel läuft.
   *
   * Getrennt von `landing` gehalten: Jene trägt die Klickparameter und kann
   * aus einem früheren Besuch stammen, diese ist immer das gerade ausgefüllte
   * Quiz. Im Leadportal steht sie als Landingpage.
   */
  private readonly seite = window.location.href;

  constructor(
    private readonly slug: string,
    private readonly abstandMs: number,
  ) {
    const merkung = merkungLesen();

    /**
     * Die Adresse, die als Herkunft gilt.
     *
     * Auf einer Anfrage-Seite ist das schlicht die aufgerufene Adresse. Steckt
     * das Quiz dagegen in einem Ratgeber, trägt der Behälter seine Herkunft
     * als data-herkunft, und sie wird hier ergänzt - sonst käme jede Anfrage
     * aus einem Beitrag als "direkt" an.
     */
    const adresse = adresseMitHerkunft(
      window.location.href,
      herkunftDesBehaelters(),
    );

    const hatEigene = traegtZuordnung(adresse);

    /**
     * Ein frischer Anzeigenklick beginnt immer eine neue Sitzung.
     *
     * Der Fall, der das nötig macht: Jemand ruft die Seite direkt auf, schaut
     * sich um, und klickt eine halbe Stunde später eine Anzeige an. Ohne diese
     * Regel liefe er in dieselbe Sitzung, die schon ohne Zuordnung angelegt
     * wurde. Die Zuordnungsspalten sind serverseitig unveränderlich, die gclid
     * ginge also stillschweigend verloren, und die Anzeige, die den Auftrag
     * gebracht hat, bekäme ihn nie zugeschrieben.
     *
     * Verglichen wird die vollständige Adresse: Ein zweiter Klick auf eine
     * andere Anzeige trägt eine andere gclid und ist ebenfalls ein neuer Besuch.
     */
    const neuerKlick = hatEigene && merkung?.landing !== adresse;
    const abgelaufen =
      merkung === null || Date.now() - merkung.zuletzt > SITZUNG_GUELTIG_MS;

    if (merkung && !abgelaufen && !neuerKlick) {
      this.sitzungId = merkung.id;
    } else if (merkung) {
      // Eine neue Sitzung, aber derselbe Browser. Der Bezug wird festgehalten,
      // damit sich eine Rückkehr später von einem Erstbesuch unterscheiden lässt.
      this.vorherigeSitzung = merkung.id;
    }

    /**
     * Bringt dieser Aufruf keine eigene Zuordnung mit, wird eine gemerkte
     * übernommen.
     *
     * Der Fall: Jemand klickt eine Anzeige an, bricht ab, und kommt am nächsten
     * Tag über ein Lesezeichen zurück. Der zweite Aufruf trägt keine gclid.
     * Ohne diese Übernahme wäre die Anfrage "direkt" und die Anzeige, die sie
     * ausgelöst hat, bekäme sie nie zugeschrieben.
     *
     * Getrennt als RESTORED gekennzeichnet: Es ist eine begründete Annahme,
     * kein Beleg, und darf in der Auswertung nicht denselben Rang haben.
     */
    const herkunft = herkunftWaehlen(adresse, document.referrer, merkung);
    this.landing = herkunft.landing;
    this.referrer = herkunft.referrer;
    this.zuordnungWiederhergestellt = herkunft.wiederhergestellt;

    merkungSchreiben({
      id: this.sitzungId,
      zuletzt: Date.now(),
      landing: this.landing,
      referrer: this.referrer,
    });
  }

  /** Die Kennung, die beim Absenden des Leads mitgeht. */
  get sessionId(): string {
    return this.sitzungId;
  }

  /**
   * Meldet die Sitzung an und startet das Bündeln.
   *
   * Wartet nicht auf die Antwort. Ereignisse, die vorher entstehen, liegen in
   * der Warteschlange und gehen mit dem ersten Bündel raus.
   */
  starten(): void {
    void (async () => {
      try {
        await fetch(`${BASIS}/api/public/sessions`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            session_id: this.sitzungId,
            funnel_slug: this.slug,
            landing_url: this.landing.slice(0, 2000),
            page_url: this.seite.slice(0, 2000),
            referrer: this.referrer.slice(0, 1000) || undefined,
            screen_width: window.screen.width,
            resumed_from: this.vorherigeSitzung ?? undefined,
            attribution_restored: this.zuordnungWiederhergestellt || undefined,
          }),
        });
        this.bereit = true;
        this.senden();
      } catch {
        // Ohne angemeldete Sitzung nimmt der Server keine Ereignisse an. Die
        // Warteschlange läuft dann ins Leere, und das ist in Ordnung: Der
        // Funnel selbst funktioniert weiter.
      }
    })();

    this.uhr = window.setInterval(() => {
      this.senden();
    }, this.abstandMs);

    /**
     * Beim Verlassen der Seite zählt jede Millisekunde.
     *
     * `visibilitychange` auf "hidden" ist das verlässlichste Signal, das es
     * gibt: `beforeunload` und `unload` feuern auf iOS oft gar nicht, wenn der
     * Nutzer die App wechselt oder den Tab schließt. `pagehide` kommt
     * zusätzlich, weil `visibilitychange` beim Zurücknavigieren ausbleiben kann.
     */
    const verlassen = () => {
      this.melden("page_exit", {
        stepId: this.aktuellerSchritt ?? undefined,
        timeOnStepMs: Date.now() - this.schrittSeit,
      });
      this.abschicken(true);
    };

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") verlassen();
    });
    window.addEventListener("pagehide", verlassen);
  }

  /** Legt ein Ereignis in die Warteschlange. */
  melden(art: Ereignisart, angabe: EreignisAngabe = {}): void {
    if (this.beendet) return;

    this.warteschlange.push({
      id: kennung(),
      type: art,
      occurred_at: new Date().toISOString(),
      ...(angabe.stepId !== undefined ? { step_id: angabe.stepId } : {}),
      ...(angabe.previousStepId !== undefined
        ? { previous_step_id: angabe.previousStepId }
        : {}),
      ...(angabe.nextStepId !== undefined ? { next_step_id: angabe.nextStepId } : {}),
      ...(angabe.stepIndex !== undefined ? { step_index: angabe.stepIndex } : {}),
      ...(angabe.timeOnStepMs !== undefined
        ? { time_on_step_ms: Math.round(angabe.timeOnStepMs) }
        : {}),
      ...(angabe.payload !== undefined ? { payload: angabe.payload } : {}),
    });

    // Eine Warteschlange, die nie geleert wird, wächst sonst unbegrenzt.
    if (this.warteschlange.length >= 40) this.senden();
  }

  /**
   * Meldet, dass ein neuer Schritt sichtbar ist, und misst die Zeit.
   *
   * Die Zeit je Schritt ist die aussagekräftigste Zahl der ganzen Erfassung:
   * Eine Frage, an der alle lange hängen, ist entweder unklar formuliert oder
   * verlangt etwas, das man nicht im Kopf hat.
   */
  schrittGesehen(stepId: string, index: number): void {
    const jetzt = Date.now();

    if (this.aktuellerSchritt !== null && this.aktuellerSchritt !== stepId) {
      this.melden("step_completed", {
        stepId: this.aktuellerSchritt,
        nextStepId: stepId,
        timeOnStepMs: jetzt - this.schrittSeit,
      });
    }

    this.aktuellerSchritt = stepId;
    this.schrittSeit = jetzt;
    this.melden("step_viewed", { stepId, stepIndex: index });
  }

  /** Die Zeit, die auf dem aktuellen Schritt vergangen ist. */
  zeitAufSchritt(): number {
    return Date.now() - this.schrittSeit;
  }

  /** Schickt alles Ausstehende und schaltet ab. */
  beenden(): void {
    this.abschicken(true);
    this.beendet = true;
    if (this.uhr !== null) window.clearInterval(this.uhr);
    try {
      window.localStorage.removeItem(SITZUNG_SCHLUESSEL);
    } catch {
      // Nicht wichtig genug, um darüber zu stolpern.
    }
  }

  private senden(): void {
    this.abschicken(false);
  }

  private abschicken(beimVerlassen: boolean): void {
    if (this.warteschlange.length === 0) return;
    if (!this.bereit && !beimVerlassen) return;

    const buendel = this.warteschlange.splice(0, 50);
    const koerper = JSON.stringify({ session_id: this.sitzungId, events: buendel });
    const ziel = `${BASIS}/api/public/events`;

    merkungSchreiben({
      id: this.sitzungId,
      zuletzt: Date.now(),
      landing: this.landing,
      referrer: this.referrer,
    });

    /*
     * sendBeacon nur auf derselben Herkunft.
     *
     * Es überlebt das Schließen des Tabs, aber es schickt **immer** Cookies
     * mit - der Browser behandelt es als "credentials: include". Bei einer
     * fremden Herkunft verlangt er dafür "Access-Control-Allow-Credentials:
     * true", und genau das setzt die API für /api/public/* bewusst nicht:
     * Sonst könnte eine fremde Seite das Sitzungscookie eines angemeldeten
     * Betreibers mitschicken.
     *
     * Die Folge war, dass auf jeder eingebetteten Seite jedes Ereignis am
     * CORS-Vorflug scheiterte. Unbemerkt, weil sendBeacon "true" zurückgibt,
     * sobald es die Nachricht in die Warteschlange gelegt hat - der
     * Fehlschlag passiert danach. Der Rückfallweg über fetch lief deshalb
     * nie an.
     *
     * fetch mit keepalive überlebt das Schließen ebenso und schickt keine
     * Cookies. Auf fremden Seiten ist es also nicht nur die sichere, sondern
     * die einzige Wahl.
     */
    const gleicheHerkunft = BASIS === "";
    if (beimVerlassen && gleicheHerkunft && typeof navigator.sendBeacon === "function") {
      // Es kann keinen content-type setzen, deshalb der Umweg über einen Blob.
      const erfolg = navigator.sendBeacon(
        ziel,
        new Blob([koerper], { type: "application/json" }),
      );
      if (erfolg) return;
      // Schlägt es fehl (etwa wegen Größenbeschränkung), bleibt der Versuch
      // über fetch.
    }

    void fetch(ziel, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: koerper,
      keepalive: beimVerlassen,
    }).catch(() => {
      // Verlorene Ereignisse sind hinnehmbar. Ein Fehler, der bis in die
      // Oberfläche durchschlägt, ist es nicht.
    });
  }
}
